import { useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import { cn, getEventColor } from '../../../lib/utils'

const formatTime = (iso) => {
  if (!iso) return ''
  return format(parseISO(iso), 'h:mm a')
}

export const DayPanel = ({ day, events, members, onEventClick, onClose }) => {
  const memberMap = useMemo(() => {
    const map = {}
    for (const m of members ?? []) {
      if (m.profiles) map[m.profiles.id] = m.profiles
    }
    return map
  }, [members])

  const dayEvents = useMemo(() => {
    if (!day) return []
    const key = format(day, 'yyyy-MM-dd')
    return events
      .filter((e) => (e.start_date ?? e.start_time)?.slice(0, 10) === key)
      .sort((a, b) => {
        if (a.is_all_day && !b.is_all_day) return -1
        if (!a.is_all_day && b.is_all_day) return 1
        return (a.start_time ?? '').localeCompare(b.start_time ?? '')
      })
  }, [day, events])

  if (!day) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[45] bg-black/20"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface rounded-t-2xl shadow-card max-h-[70dvh] flex flex-col pb-[env(safe-area-inset-bottom,0px)]">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        <div className="px-5 pb-2 flex items-center justify-between">
          <h2 className="text-base font-bold text-text-primary">
            {format(day, 'EEEE, MMMM d')}
          </h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 pb-6 space-y-2">
          {dayEvents.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-8">No events this day</p>
          ) : (
            dayEvents.map((event) => {
              const taggedMembers = (event.event_members ?? [])
                .map((em) => memberMap[em.user_id])
                .filter(Boolean)

              return (
                <button
                  key={event.id}
                  onClick={() => onEventClick(event)}
                  className="w-full text-left bg-background rounded-xl p-3 hover:bg-primary-light transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-1 self-stretch rounded-full flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: getEventColor(event) }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-text-primary leading-tight">
                        {event.title}
                      </div>

                      {!event.is_all_day && (
                        <div className="text-xs text-text-muted mt-0.5">
                          {formatTime(event.start_time)}
                          {event.end_time && ` – ${formatTime(event.end_time)}`}
                        </div>
                      )}

                      {event.is_all_day && (
                        <div className="text-xs text-text-muted mt-0.5">All day</div>
                      )}

                      {event.location && (
                        <div className="text-xs text-text-muted mt-0.5 truncate">{event.location}</div>
                      )}

                      {taggedMembers.length > 0 && (
                        <div className="flex gap-1 mt-1.5 flex-wrap">
                          {taggedMembers.map((p) => (
                            <span
                              key={p.id}
                              className="text-[10px] font-medium px-2 py-0.5 rounded-full text-white"
                              style={{ backgroundColor: p.member_color ?? '#7c6faf' }}
                            >
                              {p.display_name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              )
            })
          )}
        </div>
      </div>
    </>
  )
}
